// The settings page's pure helpers: showing a value and turning the editor's
// draft into the value sent. No DOM.
import type { AdminSchemas } from "@10twenty/api/admin";

export type Setting = AdminSchemas["Setting"];
export type SettingValue = AdminSchemas["SettingValue"];
export type SettingActor = AdminSchemas["SettingActor"];

/** The editor's state: the number as typed, the switch, or null ("none"). */
export interface SettingDraft {
  none: boolean;
  text: string;
  on: boolean;
}

/** A value as shown: "On"/"Off", "No limit" for null, else the number. */
export function valueLabel(setting: Pick<Setting, "type" | "none_label">, value: SettingValue): string {
  if (value === null) return setting.none_label ?? "None";
  if (typeof value === "boolean") return value ? "On" : "Off";
  return value.toLocaleString();
}

export function draftFor(setting: Pick<Setting, "type">, value: SettingValue): SettingDraft {
  return {
    none: value === null,
    text: typeof value === "number" ? String(value) : "",
    on: value === true,
  };
}

/** The bounds as a hint: "2–10,000", "at least 1". */
export function boundsLabel(setting: Pick<Setting, "minimum" | "maximum">): string | null {
  const { minimum, maximum } = setting;
  if (minimum !== null && maximum !== null) return `${minimum.toLocaleString()}–${maximum.toLocaleString()}`;
  if (minimum !== null) return `at least ${minimum.toLocaleString()}`;
  if (maximum !== null) return `at most ${maximum.toLocaleString()}`;
  return null;
}

/** The value a draft stands for, or why it can't be sent (the server checks again). */
export function draftValue(
  setting: Pick<Setting, "type" | "nullable" | "minimum" | "maximum">,
  draft: SettingDraft,
): { value: SettingValue } | { error: string } {
  if (setting.nullable && draft.none) return { value: null };
  if (setting.type === "boolean") return { value: draft.on };
  const text = draft.text.trim();
  if (!/^-?\d+$/.test(text)) return { error: "Enter a whole number." };
  const value = Number(text);
  const bounds = boundsLabel(setting);
  if ((setting.minimum !== null && value < setting.minimum) || (setting.maximum !== null && value > setting.maximum))
    return { error: `Enter a number ${bounds?.startsWith("at") ? bounds : `from ${bounds}`}.` };
  return { value };
}

/** Who made a change: the staff member's address, the CLI operator, or a deleted account. */
export function actorLabel(actor: SettingActor | null): string {
  if (!actor) return "—";
  if (actor.email) return actor.email;
  if (actor.user_id) return "Deleted account";
  return actor.operator ?? "—";
}
